import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet, HelmetTags } from 'react-helmet-async';
import { selectCurrentIDToken } from 'src/store/slices/auth/authSelectors';
import { selectConfigExpressChatbot } from 'src/store/slices/config/configSelectors';

const BOTPRESS_INJECT_SCRIPT_ID = 'botpress-inject';
const BOTPRESS_CLIENT_SCRIPT_ID = 'botpress-client';
const BOTPRESS_INJECT_SRC = 'https://cdn.botpress.cloud/webchat/v5.0/inject.js';
const BOTPRESS_EMBEDDED_CHAT_ID = 'bp-embedded-webchat';
// Mirrors the bot's configured theme color (customer-config express.chatbot).
const BOTPRESS_THEME_COLOR = '#3777a6';

interface BotpressWebchat {
  close: () => void;
  on: (event: string, callback: () => void) => void;
  open: () => void;
}

declare global {
  interface Window {
    botpress?: BotpressWebchat;
  }
}

export const ChatbotWidget = () => {
  const chatbot = useSelector(selectConfigExpressChatbot);
  const idToken = useSelector(selectCurrentIDToken);

  const isLoggedIn = !!idToken;
  const clientReference = chatbot?.client_reference;
  const isBotpressEnabled =
    chatbot?.provider === 'botpress' && !!clientReference;

  const [isInjectScriptLoaded, setIsInjectScriptLoaded] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isBotpressReady, setIsBotpressReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset once per config change so a place switch (or config reload) waits
  // for its own inject.js to load before requesting the client script again.
  useEffect(() => {
    setIsInjectScriptLoaded(false);
    setIsBotpressReady(false);
  }, [clientReference]);

  // The client script takes a couple of seconds to actually render Botpress's
  // panel (including its header) into our container after it loads — until
  // then our overlay close button would float over an empty box with nothing
  // to occlude. Watch the container for its first child and only show our
  // controls once Botpress has actually rendered something into it.
  useEffect(() => {
    if (!isBotpressEnabled || !isLoggedIn || !isInjectScriptLoaded) {
      return undefined;
    }
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    if (container.childNodes.length > 0) {
      setIsBotpressReady(true);
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (container.childNodes.length > 0) {
        setIsBotpressReady(true);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true });
    return () => observer.disconnect();
  }, [isBotpressEnabled, isLoggedIn, isInjectScriptLoaded]);

  // Best-effort sync only — we no longer depend on Botpress's own click
  // wiring or events to drive our open/closed state (its Embedded-mode
  // header "X" has no onClick wired up at all — verified via breakpoint,
  // and even intercepting the click ourselves proved unreliable across
  // browsers). Our own buttons below own that state directly; this just
  // keeps us aligned if the SDK's internal state ever changes some other way.
  useEffect(() => {
    if (!isBotpressEnabled || !isLoggedIn || !isInjectScriptLoaded) {
      return undefined;
    }
    window.botpress?.on('webchat:opened', () => setIsPanelOpen(true));
    window.botpress?.on('webchat:closed', () => setIsPanelOpen(false));
    // window.botpress exposes no `off` method, so these listeners can't be
    // torn down individually — harmless, since window.botpress is a
    // page-lifetime singleton and this effect only fires once per config.
    return undefined;
  }, [isBotpressEnabled, isLoggedIn, isInjectScriptLoaded]);

  const handleOpen = useCallback(() => {
    window.botpress?.open();
    setIsPanelOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    window.botpress?.close();
    setIsPanelOpen(false);
  }, []);

  // inject.js sets up `window.botpress`; the client script calls into it as
  // soon as it runs. Dynamically created <script> tags default to async, so
  // rendering both up front races the two — only add the client script tag
  // once inject.js's onload has fired, guaranteeing `window.botpress` exists.
  const helmetChange = useCallback((_: unknown, addedTags: HelmetTags) => {
    const injectScriptTag = addedTags?.scriptTags?.find(
      (tag) => tag.id === BOTPRESS_INJECT_SCRIPT_ID
    );
    if (injectScriptTag) {
      injectScriptTag.onload = () => setIsInjectScriptLoaded(true);
    }
  }, []);

  if (!isBotpressEnabled || !isLoggedIn) {
    return null;
  }

  const scripts: { id: string; src: string; defer?: string }[] = [
    { id: BOTPRESS_INJECT_SCRIPT_ID, src: BOTPRESS_INJECT_SRC },
  ];
  if (isInjectScriptLoaded) {
    scripts.push({
      id: BOTPRESS_CLIENT_SCRIPT_ID,
      src: clientReference,
      defer: 'true',
    });
  }

  return (
    <>
      <Helmet
        script={scripts}
        onChangeClientState={helmetChange}
      />
      <div
        style={{
          bottom: 0,
          display: isPanelOpen ? 'block' : 'none',
          height: '640px',
          maxHeight: '80vh',
          maxWidth: '90vw',
          position: 'fixed',
          right: 0,
          width: '380px',
          zIndex: 1000,
        }}
      >
        <div
          id={BOTPRESS_EMBEDDED_CHAT_ID}
          ref={containerRef}
          style={{ height: '100%', width: '100%' }}
        />
        {isBotpressReady && (
          <button
            aria-label="Close Chatbot"
            onClick={handleClose}
            style={{
              alignItems: 'center',
              backgroundColor: '#fff',
              border: 'none',
              color: '#333',
              cursor: 'pointer',
              display: 'flex',
              height: '40px',
              justifyContent: 'center',
              position: 'absolute',
              right: '11px',
              top: '11px',
              width: '40px',
              zIndex: 1001,
            }}
            type="button"
          >
            <svg
              aria-hidden="true"
              fill="none"
              height="20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              viewBox="0 0 24 24"
              width="20"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
      {!isPanelOpen && (
        <button
          aria-label="Open Chatbot"
          onClick={handleOpen}
          style={{
            alignItems: 'center',
            backgroundColor: BOTPRESS_THEME_COLOR,
            border: 'none',
            borderRadius: '50%',
            bottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            height: '56px',
            justifyContent: 'center',
            position: 'fixed',
            right: '20px',
            width: '56px',
            zIndex: 1000,
          }}
          type="button"
        >
          <svg
            aria-hidden="true"
            fill="none"
            height="26"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            viewBox="0 0 24 24"
            width="26"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}
    </>
  );
};
